const KEY_VALUE = /^\s*-\s+([a-zA-Z_][a-zA-Z0-9_-]*)\s*:\s*(.*)$/;
const ARRAY_ITEM = /^\s*-\s+(.+)$/;

function stripQuotes(value: string): string {
  const trimmed = value.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function parseScalar(raw: string): unknown {
  const value = raw.trim();
  if (value === "true") return true;
  if (value === "false") return false;
  if (value === "null" || value === "") return undefined;
  if (/^-?\d+$/.test(value)) return Number(value);
  return stripQuotes(value);
}

function parseInlineArray(raw: string): unknown[] {
  const inner = raw.trim().slice(1, -1).trim();
  if (inner.length === 0) return [];
  const items: unknown[] = [];
  let buf = "";
  let inQuote: string | null = null;
  for (const ch of inner) {
    if (inQuote) {
      if (ch === inQuote) inQuote = null;
      else buf += ch;
      continue;
    }
    if (ch === '"' || ch === "'") {
      inQuote = ch;
      continue;
    }
    if (ch === ",") {
      items.push(parseScalar(buf));
      buf = "";
      continue;
    }
    buf += ch;
  }
  if (buf.trim().length > 0) items.push(parseScalar(buf));
  return items;
}

function leadingSpaces(line: string): number {
  let n = 0;
  while (n < line.length && line[n] === " ") n += 1;
  return n;
}

export function parsePropertyLines(
  lines: string[],
  baseIndent = 0,
): { properties: Record<string, unknown>; consumed: number } {
  const properties: Record<string, unknown> = {};
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line === undefined) break;
    if (line.trim().length === 0) {
      i += 1;
      continue;
    }
    if (leadingSpaces(line) < baseIndent) break;

    const kv = line.match(KEY_VALUE);
    if (!kv) break;
    const key = kv[1];
    const rawValue = kv[2];
    if (key === undefined) {
      i += 1;
      continue;
    }
    const value = (rawValue ?? "").trim();

    if (value.length === 0) {
      const items: unknown[] = [];
      let j = i + 1;
      const childIndent = baseIndent + 2;
      while (j < lines.length) {
        const child = lines[j];
        if (child === undefined) break;
        if (child.trim().length === 0) {
          j += 1;
          continue;
        }
        if (leadingSpaces(child) < childIndent) break;
        const arr = child.match(ARRAY_ITEM);
        if (!arr) break;
        const raw = arr[1];
        if (raw !== undefined) items.push(parseScalar(raw));
        j += 1;
      }
      properties[key] = items;
      i = j;
      continue;
    }

    if (value.startsWith("[") && value.endsWith("]")) {
      properties[key] = parseInlineArray(value);
      i += 1;
      continue;
    }

    properties[key] = parseScalar(value);
    i += 1;
  }

  return { properties, consumed: i };
}

export function normalizeStringArray(value: unknown): string[] {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === "string" && v.trim().length > 0)
      .map((v) => v.trim());
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length === 0 ? [] : [trimmed];
  }
  return [];
}

export function normalizeRisk(value: unknown): string[] {
  return normalizeStringArray(value);
}

export function asString(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  return undefined;
}

export function asCost(value: unknown): string | boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "string" && value.trim().length > 0) return value.trim();
  return undefined;
}
